/**
 * Brush Picker
 * Reusable component for selecting colors and gradients
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Trash2, Circle, Square } from 'lucide-react';
import type {
  Brush,
  BackgroundBrush,
  GradientStop,
  SolidBrush,
  LinearGradientBrush,
  RadialGradientBrush,
} from '@/types/brush';
import { brushToCss, solid, linearGradient, radialGradient, transparent } from '@/types/brush';

type BrushType = 'solid' | 'linear-gradient' | 'radial-gradient' | 'transparent';

interface BrushPickerProps {
  value: Brush | BackgroundBrush;
  onChange: (brush: Brush) => void;
  label?: string;
  showTransparent?: boolean;
  presetColors?: string[];
}

const DEFAULT_PRESETS = [
  '#FFFFFF', '#000000', '#FF0000', '#00FF00', '#0000FF',
  '#FFFF00', '#FF00FF', '#00FFFF', '#FF6B6B', '#4ECDC4',
  '#667EEA', '#764BA2', '#F093FB', '#0A1628', '#1E293B',
];

const DEFAULT_GRADIENT_STOPS: GradientStop[] = [
  { color: '#667EEA', position: 0 },
  { color: '#764BA2', position: 100 },
];

export function BrushPicker({
  value,
  onChange,
  label,
  showTransparent = true,
  presetColors = DEFAULT_PRESETS,
}: BrushPickerProps) {
  const [activeTab, setActiveTab] = useState<BrushType>(() => {
    if (value.type === 'image' || value.type === 'sweep-gradient') return 'solid';
    return value.type;
  });

  // Get current values based on type
  const solidColor = value.type === 'solid' ? value.color : '#FFFFFF';
  const gradientStops = (value.type === 'linear-gradient' || value.type === 'radial-gradient' || value.type === 'sweep-gradient')
    ? value.stops
    : DEFAULT_GRADIENT_STOPS;
  const gradientAngle = value.type === 'linear-gradient' ? value.angle : 180;
  const centerX = (value.type === 'radial-gradient' || value.type === 'sweep-gradient') ? value.centerX : 50;
  const centerY = (value.type === 'radial-gradient' || value.type === 'sweep-gradient') ? value.centerY : 50;

  // Handle solid color change
  const handleSolidColorChange = useCallback((color: string) => {
    onChange(solid(color));
  }, [onChange]);

  // Handle gradient stop change
  const handleGradientStopChange = useCallback((index: number, updates: Partial<GradientStop>) => {
    const newStops = [...gradientStops];
    newStops[index] = { ...newStops[index], ...updates };

    if (activeTab === 'linear-gradient') {
      onChange(linearGradient(newStops, gradientAngle));
    } else if (activeTab === 'radial-gradient') {
      onChange(radialGradient(newStops, centerX, centerY));
    }
  }, [gradientStops, activeTab, gradientAngle, centerX, centerY, onChange]);

  // Add gradient stop
  const handleAddStop = useCallback(() => {
    if (gradientStops.length >= 5) return;

    // Find a position between existing stops
    const sortedStops = [...gradientStops].sort((a, b) => a.position - b.position);
    let newPosition = 50;
    for (let i = 0; i < sortedStops.length - 1; i++) {
      const gap = sortedStops[i + 1].position - sortedStops[i].position;
      if (gap > 20) {
        newPosition = sortedStops[i].position + gap / 2;
        break;
      }
    }

    const newStops = [...gradientStops, { color: '#888888', position: newPosition }];

    if (activeTab === 'linear-gradient') {
      onChange(linearGradient(newStops, gradientAngle));
    } else if (activeTab === 'radial-gradient') {
      onChange(radialGradient(newStops, centerX, centerY));
    }
  }, [gradientStops, activeTab, gradientAngle, centerX, centerY, onChange]);

  // Remove gradient stop
  const handleRemoveStop = useCallback((index: number) => {
    if (gradientStops.length <= 2) return;

    const newStops = gradientStops.filter((_, i) => i !== index);

    if (activeTab === 'linear-gradient') {
      onChange(linearGradient(newStops, gradientAngle));
    } else if (activeTab === 'radial-gradient') {
      onChange(radialGradient(newStops, centerX, centerY));
    }
  }, [gradientStops, activeTab, gradientAngle, centerX, centerY, onChange]);

  // Handle angle change
  const handleAngleChange = useCallback((angle: number) => {
    onChange(linearGradient(gradientStops, angle));
  }, [gradientStops, onChange]);

  // Handle center change
  const handleCenterChange = useCallback((x: number, y: number) => {
    onChange(radialGradient(gradientStops, x, y));
  }, [gradientStops, onChange]);

  // Handle tab change
  const handleTabChange = useCallback((tab: BrushType) => {
    setActiveTab(tab);

    if (tab === 'solid') {
      onChange(solid(solidColor));
    } else if (tab === 'linear-gradient') {
      onChange(linearGradient(gradientStops, gradientAngle));
    } else if (tab === 'radial-gradient') {
      onChange(radialGradient(gradientStops, centerX, centerY));
    } else if (tab === 'transparent') {
      onChange(transparent());
    }
  }, [solidColor, gradientStops, gradientAngle, centerX, centerY, onChange]);

  return (
    <div className="space-y-3">
      {label && <label className="label">{label}</label>}

      {/* Preview */}
      <div
        className="h-12 rounded-lg border border-white/20 relative overflow-hidden"
        style={{ background: brushToCss(value) }}
      >
        {value.type === 'transparent' && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-fg-muted">
            Transparent
          </div>
        )}
      </div>

      {/* Type tabs */}
      <div className="flex gap-1 p-1 bg-white/5 rounded-lg">
        <TabButton
          active={activeTab === 'solid'}
          onClick={() => handleTabChange('solid')}
          title="Solid Color"
        >
          <Square size={14} />
        </TabButton>
        <TabButton
          active={activeTab === 'linear-gradient'}
          onClick={() => handleTabChange('linear-gradient')}
          title="Linear Gradient"
        >
          <div className="w-4 h-4 rounded-sm bg-gradient-to-r from-blue-500 to-purple-500" />
        </TabButton>
        <TabButton
          active={activeTab === 'radial-gradient'}
          onClick={() => handleTabChange('radial-gradient')}
          title="Radial Gradient"
        >
          <Circle size={14} />
        </TabButton>
        {showTransparent && (
          <TabButton
            active={activeTab === 'transparent'}
            onClick={() => handleTabChange('transparent')}
            title="Transparent"
          >
            <div className="w-4 h-4 rounded-sm border border-white/30 bg-[repeating-conic-gradient(#333_0_90deg,#666_90deg_180deg)_0_0/8px_8px]" />
          </TabButton>
        )}
      </div>

      {/* Solid color picker */}
      {activeTab === 'solid' && (
        <div className="space-y-3">
          {/* Color input */}
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={solidColor}
              onChange={(e) => handleSolidColorChange(e.target.value)}
              className="w-10 h-10 rounded cursor-pointer border border-white/20"
            />
            <input
              type="text"
              value={solidColor}
              onChange={(e) => handleSolidColorChange(e.target.value)}
              className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-sm text-fg font-mono uppercase"
              placeholder="#FFFFFF"
            />
          </div>

          {/* Preset colors */}
          <div className="grid grid-cols-10 gap-1">
            {presetColors.map((color) => (
              <button
                key={color}
                onClick={() => handleSolidColorChange(color)}
                className={`
                  w-6 h-6 rounded border transition-all
                  ${solidColor.toUpperCase() === color.toUpperCase()
                    ? 'border-white ring-1 ring-primary scale-110'
                    : 'border-white/20 hover:border-white/50'
                  }
                `}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>
      )}

      {/* Gradient editors */}
      {(activeTab === 'linear-gradient' || activeTab === 'radial-gradient') && (
        <div className="space-y-3">
          {/* Gradient stops */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-fg-muted">Color Stops</span>
              {gradientStops.length < 5 && (
                <button
                  onClick={handleAddStop}
                  className="p-1 hover:bg-white/10 rounded text-fg-muted hover:text-fg"
                  title="Add stop"
                >
                  <Plus size={14} />
                </button>
              )}
            </div>

            {gradientStops.map((stop, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="color"
                  value={stop.color}
                  onChange={(e) => handleGradientStopChange(index, { color: e.target.value })}
                  className="w-8 h-8 rounded cursor-pointer border border-white/20"
                />
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={stop.position}
                  onChange={(e) => handleGradientStopChange(index, { position: Number(e.target.value) })}
                  className="flex-1"
                />
                <span className="w-10 text-xs text-fg-muted text-right">{stop.position}%</span>
                {gradientStops.length > 2 && (
                  <button
                    onClick={() => handleRemoveStop(index)}
                    className="p-1 hover:bg-white/10 rounded text-fg-muted hover:text-red-400"
                    title="Remove stop"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Angle (linear only) */}
          {activeTab === 'linear-gradient' && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-fg-muted">Angle</span>
                <span className="text-xs text-fg">{gradientAngle}°</span>
              </div>
              <input
                type="range"
                min={0}
                max={360}
                value={gradientAngle}
                onChange={(e) => handleAngleChange(Number(e.target.value))}
                className="w-full"
              />
            </div>
          )}

          {/* Center position (radial only) */}
          {activeTab === 'radial-gradient' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-fg-muted">Center X</span>
                  <span className="text-xs text-fg">{centerX}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={centerX}
                  onChange={(e) => handleCenterChange(Number(e.target.value), centerY)}
                  className="w-full"
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-fg-muted">Center Y</span>
                  <span className="text-xs text-fg">{centerY}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={centerY}
                  onChange={(e) => handleCenterChange(centerX, Number(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Transparent info */}
      {activeTab === 'transparent' && (
        <p className="text-xs text-fg-subtle text-center py-4">
          No fill or background will be applied
        </p>
      )}
    </div>
  );
}

// Tab button component
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}

function TabButton({ active, onClick, title, children }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`
        flex-1 flex items-center justify-center p-2 rounded transition-colors
        ${active
          ? 'bg-primary/20 text-primary'
          : 'text-fg-muted hover:bg-white/5 hover:text-fg'
        }
      `}
      title={title}
    >
      {children}
    </button>
  );
}

// Compact color picker for inline use
interface ColorSwatchProps {
  value: Brush;
  onChange: (brush: Brush) => void;
  size?: 'sm' | 'md';
}

export function ColorSwatch({ value, onChange, size = 'md' }: ColorSwatchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const sizeClass = size === 'sm' ? 'w-6 h-6' : 'w-8 h-8';

  // Update popup position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const popupWidth = 256;
      const popupHeight = 400;

      let left = rect.left;
      let top = rect.bottom + 8;

      // Keep popup on screen horizontally
      if (left + popupWidth > window.innerWidth - 16) {
        left = window.innerWidth - popupWidth - 16;
      }

      // If popup would go below viewport, show it above the button instead
      if (top + popupHeight > window.innerHeight - 16) {
        top = rect.top - popupHeight - 8;
        if (top < 16) top = 16;
      }

      setPopupStyle({ top, left });
    }
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(true)}
        className={`${sizeClass} rounded border border-white/20 cursor-pointer transition-all hover:border-white/50`}
        style={{ background: brushToCss(value) }}
        title="Click to edit color"
      />

      {isOpen && createPortal(
        <>
          <div
            className="fixed inset-0"
            style={{ zIndex: 9998 }}
            onClick={() => setIsOpen(false)}
          />
          <div
            className="fixed w-64 p-3 bg-navy-light border border-white/10 rounded-lg shadow-xl"
            style={{ zIndex: 9999, ...popupStyle }}
          >
            <BrushPicker
              value={value}
              onChange={onChange}
              showTransparent={true}
            />
          </div>
        </>,
        document.body
      )}
    </>
  );
}
